import { Link, useLocation } from '@tanstack/react-router'
import { Blocks, ChevronLeft, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { NotificationPanel } from '@/features/notification/components/notification-panel'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { Logo } from '@/shared/components/logo'
import { Button } from '@/shared/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
  useSidebar,
} from '@/shared/ui/sidebar'
import { ApplicationList } from './application-list'
import { UserMenu } from './user-menu'

export function AppSidebar() {
  const { t } = useTranslation()
  const { workspace, isPending } = useTenantWorkspace()
  const pathname = useLocation({ select: (location) => location.pathname })
  const { toggleSidebar, isMobile } = useSidebar()

  return (
    // Narrowed to its icons rather than taken off the screen, so what is left
    // behind is still the way around the workspace rather than a blank edge.
    <Sidebar collapsible="icon" className="z-30 shadow-sm ring-1 ring-foreground/10">
      {/*
        The application's name rather than a control. There is one workspace and
        nothing to switch to, and the entry below already goes to the one place
        this would have led.
      */}
      <SidebarHeader className="justify-center border-b py-0 pr-2 pl-6 group-data-[collapsible=icon]:px-0">
        <div className="flex h-14 items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <Logo className="size-5 shrink-0 mb-1" />
          <span className="truncate text-lg font-bold group-data-[collapsible=icon]:hidden">
            {t('brand.name')}
            <span className="ml-1.5 underline">{t('brand.app')}</span>
          </span>
          {/* Shown on hover, so it stays out of the way until somebody reaches
              for the sidebar. The page bar holds the arrow that reopens it. */}
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('action.collapseSidebar')}
              onClick={toggleSidebar}
              className="ml-auto size-8 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 group-data-[collapsible=icon]:hidden"
            >
              <ChevronLeft />
            </Button>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-none">
        <SidebarGroup>
          <SidebarGroupLabel>{t('workspace.general')}</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              {/*
                Named after the tenant, whose name arrives with the workspace list.
                Skeletonised until it does, because the alternative first paint is
                an empty row.
              */}
              {isPending ? (
                <SidebarMenuSkeleton showIcon />
              ) : (
                <SidebarMenuButton
                  asChild
                  isActive={pathname === '/dashboard'}
                  tooltip={workspace?.name ?? ''}
                >
                  <Link to="/dashboard">
                    <Home />
                    <span className="truncate">{workspace?.name}</span>
                  </Link>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={pathname.startsWith('/templates')}
                tooltip={t('templates.nav')}
              >
                <Link to="/templates">
                  <Blocks />
                  <span>{t('templates.nav')}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {workspace !== undefined && <ApplicationList workspaceId={workspace.id} />}
      </SidebarContent>

      <SidebarFooter className="border-t pb-4">
        {/* Stacked once there is only icon width for them, which is the one
            arrangement that fits both. */}
        <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
          <div className="min-w-0 flex-1">
            <UserMenu />
          </div>
          <NotificationPanel />
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
